import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
	MuiPagination,
	MuiPaginationFilter,
	MuiPaginationInfo,
	MuiPaginationIterator,
	MuiPaginationLoadingIndicator,
	MuiPaginationPageSelector,
	MuiPaginationSort,
} from '@lsoft/mui';
import Grid from '@material-ui/core/Grid';
import { ShopGridProduct } from './ShopGridProduct';
import _ from 'lodash';
import { useRouter } from 'next/router';
import {
	trNewestFirst,
	trThereAreCurrentlyNoProductsAvailableInThisCategory,
} from './Product.tr';
import { MuiPaginationEmptyAlert } from '../../../libs/mui/MuiPaginationEmptyAlert';
import { ShopProductContainer } from '../../container/shop/ShopProduct';
import {
	trOnlyGoodsInStock,
	trPriceAscending,
	trPriceDescending,
} from '@lsoft/shared/translations/product.tr';
import { product_internet_stock_amount_threshold } from '@lsoft/shared/functions/shop/product';
import { renderedProductsPaginationView } from '@lsoft/shared/api/render_products';

const Top = () => {
	const internet_stock_amount_threshold = useMemo(() => {
		return product_internet_stock_amount_threshold();
	}, []);

	return (
		<Grid
			container
			direction={'row'}
			justifyContent={'space-between'}
			alignItems={'center'}
		>
			<Grid item>
				<Grid container direction={'row'} spacing={2}>
					<Grid item>
						<MuiPaginationSort />
					</Grid>
					<Grid item>
						<MuiPaginationFilter
							label={trOnlyGoodsInStock}
							filter={{
								match: {
									fields: {
										stock_available: {
											$gte: internet_stock_amount_threshold,
										},
									},
								},
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item>
				<Grid container direction={'row'} alignItems={'center'} spacing={2}>
					<Grid item>
						<MuiPaginationInfo />
					</Grid>
					<Grid item>
						<MuiPaginationPageSelector />
					</Grid>
				</Grid>
			</Grid>
			<MuiPaginationLoadingIndicator />
		</Grid>
	);
};

const Bottom = () => {
	return (
		<Grid
			container
			direction={'row'}
			justifyContent={'flex-end'}
			alignItems={'center'}
		>
			<Grid item>
				<Grid container direction={'row'} alignItems={'center'} spacing={2}>
					<Grid item>
						<MuiPaginationInfo />
					</Grid>
					<Grid item>
						<MuiPaginationPageSelector />
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

const transform_sort_dict_to_string = (sort) => {
	const temp = Object.entries(sort).map(([key, value]) => {
		return value > 0 ? `${key}` : `-${key}`;
	});
	return temp.join(',');
};

const PaginationContent = () => {
	return (
		<MuiPaginationEmptyAlert
			tr={trThereAreCurrentlyNoProductsAvailableInThisCategory}
		>
			<Grid container direction={'row'} spacing={2}>
				<Grid item xs={12}>
					<Top />
				</Grid>
				<Grid item xs={12}>
					<Grid container direction={'row'} spacing={2}>
						<MuiPaginationIterator renderRow={renderItem} />
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Bottom />
				</Grid>
			</Grid>
		</MuiPaginationEmptyAlert>
	);
};

export const renderItem = (sku, product) => {
	const product_container_props = _.assign(
		{},
		{
			sku,
			// view: 'shop_grid',
			view: 'full',
		},
		_.isNil(product)
			? null
			: {
					data: product,
				},
	);
	return (
		<Grid item key={sku} xs={12} sm={4} md={4} lg={3}>
			<ShopProductContainer {...product_container_props}>
				<ShopGridProduct />
			</ShopProductContainer>
		</Grid>
	);
};

export const ShopProductGroupGrid = ({
	grouptags,
	onPageChange,
	paginationViewParams,
}) => {
	const router = useRouter();

	if (_.isNil(grouptags) || grouptags.length === 0) return null;

	const key = _.join(grouptags, ',');
	const options = _.assign(
		{},
		{
			itemsPerPageDefault: 24,
			itemsPerPageOptions: [12, 24, 48],
			SKIP: 0,
			SORT: {
				sku: -1,
			},
			LIMIT: 24,
		},
		paginationViewParams,
	);
	return (
		<MuiPagination
			key={key}
			router={router}
			loadData={async (SKIP, LIMIT, SORT, OPTIONS) => {
				const stock_available_filter = _.get(
					OPTIONS,
					'FILTER.match.fields.stock_available',
					null,
				);
				const filter = _.assign(
					{},
					{
						group_tags: grouptags,
					},
					!_.isNil(stock_available_filter)
						? { stock_available: stock_available_filter }
						: {},
				);
				const params = {
					filter: filter,
					skip: SKIP,
					limit: LIMIT,
					sort: transform_sort_dict_to_string(SORT),
					// view: 'shop_grid',
					view: 'full',
				};
				return await renderedProductsPaginationView(params);
			}}
			options={options}
			defaultSortOption={'newest_first'}
			sortOptions={[
				{
					value: 'newest_first',
					realValue: {
						sku: -1,
					},
					label: trNewestFirst,
				},
				{
					value: 'price_up',
					realValue: {
						'data.netto_sp': 1,
					},
					label: trPriceAscending,
				},
				{
					value: 'price_down',
					realValue: {
						'data.netto_sp': -1,
					},
					label: trPriceDescending,
				},
			]}
			onPageChange={() => {
				onPageChange();
			}}
		>
			<PaginationContent />
		</MuiPagination>
	);
};

ShopProductGroupGrid.propTypes = {
	grouptags: PropTypes.array,
	paginationViewParams: PropTypes.object,
	onPageChange: PropTypes.func,
};

ShopProductGroupGrid.defaultProps = {
	grouptags: [],
	onPageChange: () => {},
};
